import { Box } from "@mui/material";
import React, { ReactNode } from "react";
import { SamplesSpatialIcon, SamplesTemporalIcon, ROIDataIcon, ROIDisplayIcon, ColorSpaceIcon, StopwatchIcon, ShutterAngleIcon } from "../../../CustomIcons/CustomIcons";

interface OverlayProps {
	version_metadata?: any;
	fio_asset_info?: any;
	curFrame?: number;
	overlaySize: { width: number; height: number };
	overscanPercentage?: number;
	overscanZoomEnabled?: boolean;
	mode?: "default" | "overscan" | "both";
	infoState?: "none" | "compact" | "metadata" | null;
	gridState?: "none" | "grid" | "overscangrid" | null;
}

interface OverlayContainerProps {
	scale: number;
	overlaySize: { width: number; height: number };
	children: ReactNode;
	info?: ReactNode;
}

const OverlayContainer: React.FC<OverlayContainerProps> = ({ scale, overlaySize, children, info }) => {
	const opacity = overlaySize.height > 0 && overlaySize.width > 0 ? 1 : 0;

	return (
		<div
			className="markerOverlayContainer"
			style={{
				position: "absolute",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				top: 51,
				left: 0,
				width: "100%",
				height: "calc(100% - 196px - 51px)",
				opacity,
				pointerEvents: "none",
				zIndex: 1600,
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					transform: `scale(${scale})`,
					transformOrigin: "center",
					width: overlaySize.width,
					height: overlaySize.height,
				}}
			>
				{children}
			</div>

			{info && (
				<div
					style={{
						position: "absolute",
						display: "flex",
						justifyContent: "center",
						width: overlaySize.width,
						height: overlaySize.height,
						paddingTop: ".5em",
						fontSize: ".85em",
					}}
				>
					{info}
				</div>
			)}
		</div>
	);
};

interface ElementContainerProps {
	overlaySize: { width: number; height: number };
	children: ReactNode;
}

const ElementContainer: React.FC<ElementContainerProps> = ({ overlaySize, children }) => (
	<div
		className="elementContainer"
		style={{
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			zIndex: 100000000,
			position: "absolute",
			height: `${overlaySize.height}px`,
			width: `${overlaySize.width}px`,
		}}
	>
		{children}
	</div>
);

interface GridProps {
	overlaySize: { width: number; height: number };
	borderSize: number;
	showOuterBorder?: boolean;
}
const Grid: React.FC<GridProps> = ({ overlaySize, borderSize, showOuterBorder = true }) => {
	const borderSizeScaled = borderSize;
	const outerBorderSize = borderSizeScaled * 2;

	const renderRow = (isMiddle: boolean) => (
		<div style={{ height: "calc(100% / 3)", display: "flex", width: "100%" }}>
			{[0, 1, 2].map((col) => {
				const borderWidths = isMiddle ? `${borderSizeScaled}px ${col === 0 ? 0 : 0}px ${borderSizeScaled}px ${col === 0 ? 0 : borderSizeScaled}px` : col === 0 ? "0px 0px 0px 0px" : `0px 0px 0px ${borderSizeScaled}px`;

				return (
					<div
						key={col}
						style={{
							height: "100%",
							width: "100%",
							border: `${borderSizeScaled}px solid white`,
							borderWidth: borderWidths,
							boxSizing: "border-box",
						}}
					/>
				);
			})}
		</div>
	);

	return (
		<div
			style={{
				position: "relative",
				height: overlaySize.height,
				width: overlaySize.width,
				display: "flex",
				flexDirection: "column",
				boxSizing: "border-box",
			}}
		>
			{renderRow(false)}
			{renderRow(true)}
			{renderRow(false)}

			{showOuterBorder && (
				<div
					style={{
						position: "absolute",
						inset: 0,
						border: `${outerBorderSize}px solid rgb(16, 185, 129)`,
						boxSizing: "border-box",
						pointerEvents: "none",
						zIndex: 10,
					}}
				/>
			)}
		</div>
	);
};

interface ResolutionInput {
	data?: { x?: number; y?: number };
	disp?: { x?: number; y?: number };
}

interface SampleData {
	temporalSampleCount?: string | number | null;
	spatialSampleCount?: string | number | null;
}

type OcioInfo = {
	destination_display_view?: string | null;
	destination_color_space?: string | null;
	source_color_space?: string | null;
};

type RenderMeta = {
	shot?: {
		output_type?: string | null;
		colorSpace?: { destination?: string | null };
		camera?: {
			overscanPercentage?: string | number | null;
			shutterAngle?: string | number | null;
			PathTracer?: {
				overscanPercent?: string | number | null;
				sensorWidth?: string | number | null;
				sensorHeight?: string | number | null;
				fov?: string | number | null;
				focalLength?: string | number | null;
				fstop?: string | number | null;
			};
		};
		resolution?: ResolutionInput;
		aa?: SampleData;
	};
	frames?: {
		render_times?: Array<number | string | null>;
		frameRenderStartTimeUTC?: string;
		frameRenderEndTimeUTC?: string;
	};
};

function parseNumberish(value: unknown): number | null {
	if (typeof value === "number" && Number.isFinite(value)) return value;

	if (typeof value === "string") {
		const cleaned = value.trim().replace("%", "");
		if (!cleaned) return null;

		const parsed = Number(cleaned);
		if (Number.isFinite(parsed)) return parsed;
	}

	return null;
}

function parseOverscanValue(value: unknown): number | null {
	const parsed = parseNumberish(value);
	if (parsed === null || parsed < 0) return null;

	if (typeof value === "string" && value.includes("%")) {
		return parsed / 100;
	}

	if (parsed > 1) {
		return parsed / 100;
	}

	return parsed;
}

function resolveOverscanFromResolution(resolution?: ResolutionInput | null): number | null {
	const dataX = Number(resolution?.data?.x) || 0;
	const dataY = Number(resolution?.data?.y) || 0;
	const dispX = Number(resolution?.disp?.x) || 0;
	const dispY = Number(resolution?.disp?.y) || 0;

	const ratios: number[] = [];

	if (dataX > 0 && dispX > 0 && dataX > dispX) {
		ratios.push(dataX / dispX - 1);
	}

	if (dataY > 0 && dispY > 0 && dataY > dispY) {
		ratios.push(dataY / dispY - 1);
	}

	if (!ratios.length) return null;

	return Math.max(...ratios);
}

function resolveOverscanPercentage(propOverscanPercentage?: number, renderMeta?: RenderMeta | null, resolution?: ResolutionInput | null): number {
	const propValue = parseOverscanValue(propOverscanPercentage);
	if (propValue !== null && propValue > 0) return propValue;

	const pathTracerValue = parseOverscanValue(renderMeta?.shot?.camera?.PathTracer?.overscanPercent);
	if (pathTracerValue !== null && pathTracerValue > 0) return pathTracerValue;

	const cameraValue = parseOverscanValue(renderMeta?.shot?.camera?.overscanPercentage);
	if (cameraValue !== null && cameraValue > 0) return cameraValue;

	const resolutionValue = resolveOverscanFromResolution(resolution);
	if (resolutionValue !== null && resolutionValue > 0) return resolutionValue;

	return 0;
}

function formatResolutionSafe(input?: ResolutionInput | null, displayWindowScale?: number | null): JSX.Element | null {
	const dataX = input?.data?.x ?? 0;
	const dataY = input?.data?.y ?? 0;
	const dispX = input?.disp?.x ?? 0;
	const dispY = input?.disp?.y ?? 0;

	if (dataX === 0 && dataY === 0 && dispX === 0 && dispY === 0) return null;

	const scale = displayWindowScale ?? 1;
	const hasOverscan = Math.abs(scale - 1) > 0.0001;
	const overscanMultiplier = hasOverscan ? 1 / scale : 1;
	const overscanPercent = Math.round((overscanMultiplier - 1) * 100);

	return (
		<span
			style={{
				display: "inline-flex",
				gap: ".25em",
				height: "1em",
			}}
		>
			<span style={{ fontSize: "1em", display: "inline-flex", alignItems: "center" }}>
				<Box component={ROIDataIcon} sx={{ mr: 0.5, width: ".75em", color: "white" }} /> {dataX}x{dataY} {hasOverscan && `(${dispX}x${dispY})`}
			</span>

			{hasOverscan && (
				<span style={{ fontSize: "1em", display: "inline-flex", alignItems: "center" }}>
					<Box component={ROIDisplayIcon} sx={{ mr: 0.5, width: ".75em", color: "white" }} /> {overscanMultiplier.toFixed(2)}x ({overscanPercent}% OS)
				</span>
			)}
		</span>
	);
}

function formatSampleCountSafe(sample?: SampleData | null): JSX.Element | null {
	if (!sample) return null;

	const temporal = Number(sample.temporalSampleCount) || 0;
	const spatial = Number(sample.spatialSampleCount) || 0;

	if (temporal === 0 && spatial === 0) return null;

	return (
		<span
			style={{
				display: "inline-flex",
				gap: ".25em",
				height: "1em",
			}}
		>
			<span style={{ fontSize: "1em", display: "inline-flex", alignItems: "center" }}>
				<Box component={SamplesTemporalIcon} sx={{ mr: 0.5, width: ".75em", color: "white" }} /> {temporal}
			</span>

			<span style={{ fontSize: "1em", display: "inline-flex", alignItems: "center" }}>
				<Box component={SamplesSpatialIcon} sx={{ mr: 0.5, width: ".75em", color: "white" }} /> {spatial}
			</span>
		</span>
	);
}

function resolveColorSpace(ocioInfo: OcioInfo | null | undefined, renderMeta: RenderMeta | null | undefined): string | null {
	const safeTrim = (s: unknown) => (typeof s === "string" ? s.trim() : "");

	if (!ocioInfo && !renderMeta) return null;

	const view = safeTrim(ocioInfo?.destination_display_view);
	if (view) return view;

	const destCS = safeTrim(ocioInfo?.destination_color_space);
	if (destCS) return destCS;

	const shotCS = safeTrim(renderMeta?.shot?.colorSpace?.destination);
	if (shotCS) return shotCS;

	const src = safeTrim(ocioInfo?.source_color_space);
	if (src) return src;

	return null;
}

function formatColorSpaceSafe(ocioInfo?: OcioInfo | null, renderMeta?: RenderMeta | null): JSX.Element | null {
	const cs = resolveColorSpace(ocioInfo ?? null, renderMeta ?? null);
	if (!cs) return null;

	return (
		<span
			style={{
				display: "inline-flex",
				gap: ".25em",
				alignItems: "center",
				height: "1em",
			}}
			title={cs}
			aria-label={`Color space: ${cs}`}
		>
			<span style={{ fontSize: "1em", display: "inline-flex", alignItems: "center" }}>
				<Box component={ColorSpaceIcon} sx={{ mr: 0.5, width: ".75em", color: "white" }} /> {cs}
			</span>
		</span>
	);
}

function parseRenderTimestampUTC(value?: string | null): Date | null {
	if (!value || typeof value !== "string") return null;

	const match = value.trim().match(/^(\d{4})[.-](\d{2})[.-](\d{2})[-T](\d{2})[.:](\d{2})[.:](\d{2})$/);
	if (!match) return null;

	const [, year, month, day, hour, minute, second] = match;

	const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)));

	return Number.isNaN(date.getTime()) ? null : date;
}

function getRenderDurationSeconds(renderMeta?: RenderMeta | null): number | null {
	const start = parseRenderTimestampUTC(renderMeta?.frames?.frameRenderStartTimeUTC);
	const end = parseRenderTimestampUTC(renderMeta?.frames?.frameRenderEndTimeUTC);

	if (!start || !end) return null;

	const diffSeconds = (end.getTime() - start.getTime()) / 1000;
	return diffSeconds >= 0 ? diffSeconds : null;
}

function getFrameRenderTimesSeconds(renderMeta?: RenderMeta | null): number[] {
	const rawTimes = renderMeta?.frames?.render_times;
	if (!Array.isArray(rawTimes)) return [];

	return rawTimes.map((value) => parseNumberish(value)).filter((value): value is number => value !== null && Number.isFinite(value) && value >= 0);
}

function formatDuration(totalSeconds: number): string {
	if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "";

	if (totalSeconds < 60) {
		return `${totalSeconds.toFixed(1)} sec`;
	}

	const secondsInt = Math.round(totalSeconds);
	const hours = Math.floor(secondsInt / 3600);
	const minutes = Math.floor((secondsInt % 3600) / 60);
	const seconds = secondsInt % 60;

	const minutesStr = String(minutes).padStart(2, "0");
	const secondsStr = String(seconds).padStart(2, "0");

	if (hours > 0) {
		return `${hours}:${minutesStr}:${secondsStr} hr`;
	}

	return `${minutesStr}:${secondsStr} min`;
}

function formatRenderTimeSafe(renderMeta?: RenderMeta | null, fioAssetData?: any | null): JSX.Element | null {
	const frameTimes = getFrameRenderTimesSeconds(renderMeta);

	const totalDurationFromStartEnd = getRenderDurationSeconds(renderMeta);
	const totalDurationFromFrames = frameTimes.length ? frameTimes.reduce((sum, time) => sum + time, 0) : null;

	const totalSeconds = totalDurationFromStartEnd ?? totalDurationFromFrames;
	if (totalSeconds === null) return null;

	let avgSecondsPerFrame: number | null = null;

	if (frameTimes.length > 0) {
		avgSecondsPerFrame = frameTimes.reduce((sum, time) => sum + time, 0) / frameTimes.length;
	} else {
		const frameCount = Number(fioAssetData?.frames) || 0;
		if (frameCount > 0) {
			avgSecondsPerFrame = totalSeconds / frameCount;
		}
	}

	const totalFormatted = formatDuration(totalSeconds);
	const avgFormatted = avgSecondsPerFrame !== null ? formatDuration(avgSecondsPerFrame) : null;

	return (
		<span
			style={{
				display: "inline-flex",
				gap: ".25em",
				alignItems: "center",
				height: "1em",
			}}
			title={frameTimes.length > 0 ? `Average based on render_metadata.frames.render_times (${frameTimes.length} frames)` : "Average based on total duration / asset frame count"}
		>
			<span style={{ fontSize: "1em", display: "inline-flex", alignItems: "center" }}>
				<Box component={StopwatchIcon} sx={{ mr: 0.5, width: ".75em", color: "white" }} /> {totalFormatted}
				{avgFormatted && <span style={{ marginLeft: ".25em", fontSize: "0.75em" }}>(avg: {avgFormatted}/frame)</span>}
			</span>
		</span>
	);
}

function formatShutterAngleSafe(renderMeta: RenderMeta | null, sample: SampleData | null): JSX.Element | null {
	const shutterAngle = parseNumberish(renderMeta?.shot?.camera?.shutterAngle);
	if (!sample || shutterAngle === null) return null;

	const temporal = Number(sample.temporalSampleCount) || 0;
	if (temporal === 0) return null;

	const temporalShutterAngle = Math.round(shutterAngle * 100 * temporal) / 100;
	const unrealValue = Math.round((shutterAngle * temporal * 100) / 360) / 100;

	return (
		<span
			style={{
				display: "inline-flex",
				gap: ".25em",
				alignItems: "center",
				height: "1em",
			}}
		>
			<span style={{ fontSize: "1em", display: "inline-flex", alignItems: "center" }}>
				<Box component={ShutterAngleIcon} sx={{ mr: 0.5, width: ".75em", color: "white" }} /> {`${temporalShutterAngle.toFixed(2)}° (${unrealValue})`}
			</span>
		</span>
	);
}

function combineInfo(resolution?: ResolutionInput | null, sample?: SampleData | null, displayWindowScale?: number | null, ocioInfo?: OcioInfo | null, renderMeta?: RenderMeta | null, fioAssetData?: any | null): JSX.Element | null {
	const resElement = formatResolutionSafe(resolution, displayWindowScale);
	const sampleElement = formatSampleCountSafe(sample);
	const colorSpaceElement = formatColorSpaceSafe(ocioInfo, renderMeta);
	const renderTimeElement = formatRenderTimeSafe(renderMeta, fioAssetData);
	const shutterAngleElement = formatShutterAngleSafe(renderMeta ?? null, sample ?? null);

	if (!resElement && !sampleElement && !colorSpaceElement && !renderTimeElement && !shutterAngleElement) {
		return null;
	}

	return (
		<div
			style={{
				display: "flex",
				alignItems: "flex-start",
				justifyContent: "center",
				gap: ".5em",
				flexWrap: "wrap",
				width: "100%",
				boxSizing: "border-box",
				height: "1em",
				paddingTop: ".5em",
			}}
		>
			{resElement}
			{sampleElement}
			{shutterAngleElement}
			{colorSpaceElement}
			{renderTimeElement}
		</div>
	);
}

const MarkerOverlayComponent: React.FC<OverlayProps> = ({ version_metadata, fio_asset_info, curFrame, overlaySize, overscanPercentage = 0, overscanZoomEnabled = false, infoState = "none", gridState = "none" }) => {
	const borderSize = 1;

	const { render_metadata = {}, ocio_info = {} } = version_metadata ?? {};
	const { shot = {} } = render_metadata as RenderMeta;
	const { resolution = {}, aa = {} } = shot ?? {};

	const resolvedOverscanPercentage = resolveOverscanPercentage(overscanPercentage, render_metadata, resolution);
	const displayWindowScale = 1 / (1 + resolvedOverscanPercentage);

	const resolvedGridState = resolvedOverscanPercentage === 0 && gridState === "overscangrid" ? "grid" : gridState;

	let info_text: JSX.Element | null = null;

	if (infoState === "compact") {
		info_text = combineInfo(resolution, aa, displayWindowScale, ocio_info, render_metadata, fio_asset_info);
	}

	const renderOverlay = (scale: number, showOuterBorder: boolean, renderGrid = true) => {
		const borderSizeScaled = borderSize / scale;

		return (
			<>
				<OverlayContainer scale={scale} overlaySize={overlaySize} info={info_text}>
					{renderGrid && (
						<ElementContainer overlaySize={overlaySize}>
							<Grid overlaySize={overlaySize} borderSize={borderSizeScaled} showOuterBorder={showOuterBorder} />
						</ElementContainer>
					)}
				</OverlayContainer>

				{renderGrid && (
					<OverlayContainer scale={1} overlaySize={overlaySize}>
						<div
							className="plus alt"
							style={{
								marginTop: "-2px",
								marginLeft: "-2px",
								width: "75px",
								height: "75px",
							}}
						/>
					</OverlayContainer>
				)}
			</>
		);
	};

	if (overscanZoomEnabled) {
		return (
			<>
				{infoState !== "none" && resolvedGridState === "none" && renderOverlay(1, false, false)}
				{resolvedGridState !== "none" && renderOverlay(1, true)}
			</>
		);
	}

	return (
		<>
			{infoState !== "none" && resolvedGridState === "none" && renderOverlay(1, false, false)}
			{resolvedGridState === "grid" && renderOverlay(1, false)}
			{resolvedGridState === "overscangrid" && renderOverlay(displayWindowScale, true)}
		</>
	);
};

export default React.memo(MarkerOverlayComponent, (prev, next) => {
	return (
		prev.curFrame === next.curFrame &&
		prev.overscanPercentage === next.overscanPercentage &&
		prev.overscanZoomEnabled === next.overscanZoomEnabled &&
		prev.mode === next.mode &&
		prev.overlaySize.width === next.overlaySize.width &&
		prev.overlaySize.height === next.overlaySize.height &&
		prev.version_metadata === next.version_metadata &&
		prev.fio_asset_info === next.fio_asset_info &&
		prev.infoState === next.infoState &&
		prev.gridState === next.gridState
	);
});
